import { inject, Injectable, InjectionToken } from '@angular/core';

import { environment } from 'src/environments/environment';
import { OrderRoot, VippsLogin } from '../model/cardOrder';
import type { PurchaserSnapshot } from '../pages/customize-card/components/purchaser-step/purchaser-step.component';

export interface VippsLoginConfig {
  clientId: string | null;
  authorizationEndpoint: string;
}

export const VIPPS_LOGIN_CONFIG = new InjectionToken<VippsLoginConfig>('VIPPS_LOGIN_CONFIG', {
  providedIn: 'root',
  factory: () => environment.vippsLogin,
});

// Name only: asking for e-mail makes a first-time Vipps Login user confirm it in
// the app mid-checkout, and the order keeps the e-mail the buyer typed anyway.
export const VIPPS_LOGIN_SCOPE = 'openid name';

export const VIPPS_CALLBACK_PATH = 'vipps-callback';

const DRAFT_PREFIX = 'vippsCheckout:';
const DRAFT_TTL_MS = 30 * 60 * 1000;

/** The finished checkout, kept while the buyer is away at Vipps. */
export interface InvoiceCheckout {
  returnPath: string;
  queryParams: Record<string, string>;
  // Validated before the buyer left: on return it is sent as it stands.
  order: OrderRoot;
  termsAccepted: boolean;
  currentActiveDesign: number;
  purchaser: PurchaserSnapshot;
}

/** What Vipps sent back: a one-time code on approval, an error otherwise. */
export interface VippsOutcome {
  code: string | null;
  error: string | null;
}

export interface InvoiceCheckoutDraft extends InvoiceCheckout {
  savedAt: number;
  codeVerifier: string;
  nonce: string;
  redirectUri: string;
  // Set by the callback, so the code never travels on in a URL.
  outcome?: VippsOutcome;
}

/**
 * Confirms who places an invoice order, with Vipps Login.
 *
 * The browser only starts the login and carries the one-time code back;
 * api-appservice swaps it, checks the ID token and takes the orderer's name from
 * it. The code lives two to five minutes, so the order is sent the moment the
 * buyer returns, and everything is validated before they leave.
 */
@Injectable({ providedIn: 'root' })
export class VippsLoginService {
  private config = inject(VIPPS_LOGIN_CONFIG);

  // A buyer who never comes back leaves their checkout behind: clear out
  // expired ones whenever the shop loads, not only on the next Vipps start.
  constructor() {
    this.purgeExpired();
  }

  get enabled(): boolean {
    return !!this.config.clientId;
  }

  /** Saves the checkout under a fresh `state` and leaves the page for Vipps. */
  async start(checkout: InvoiceCheckout): Promise<void> {
    if (!this.config.clientId) throw new Error('Vipps Login is not configured');

    const state = randomToken(24);
    const nonce = randomToken(24);
    const codeVerifier = randomToken(48);
    const redirectUri = this.redirectUri();
    const draft: InvoiceCheckoutDraft = { ...checkout, savedAt: Date.now(), codeVerifier, nonce, redirectUri };
    localStorage.setItem(DRAFT_PREFIX + state, JSON.stringify(draft));

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: VIPPS_LOGIN_SCOPE,
      state,
      nonce,
      code_challenge: await sha256Base64Url(codeVerifier),
      code_challenge_method: 'S256',
    });
    this.leaveFor(`${this.config.authorizationEndpoint}?${params.toString()}`);
  }

  peekDraft(state: string): InvoiceCheckoutDraft | null {
    const raw = localStorage.getItem(DRAFT_PREFIX + state);
    if (!raw) return null;
    try {
      const draft = JSON.parse(raw) as InvoiceCheckoutDraft;
      return Date.now() - draft.savedAt <= DRAFT_TTL_MS ? draft : null;
    } catch {
      return null;
    }
  }

  /** False when there is no live draft for this `state`. */
  recordOutcome(state: string, outcome: VippsOutcome): boolean {
    const draft = this.peekDraft(state);
    if (!draft) return false;
    localStorage.setItem(DRAFT_PREFIX + state, JSON.stringify({ ...draft, outcome }));
    return true;
  }

  /** Single use: a draft is removed the moment it is read back. */
  takeDraft(state: string): InvoiceCheckoutDraft | null {
    const draft = this.peekDraft(state);
    localStorage.removeItem(DRAFT_PREFIX + state);
    return draft;
  }

  loginFor(draft: InvoiceCheckoutDraft, code: string): VippsLogin {
    return { code, codeVerifier: draft.codeVerifier, nonce: draft.nonce, redirectUri: draft.redirectUri };
  }

  /**
   * One callback path at the root of whichever host the shop runs on. Localhost
   * and staging build from the same environment file, so it cannot be configured
   * there; api-appservice checks it against its own list.
   */
  redirectUri(): string {
    return `${window.location.origin}/${VIPPS_CALLBACK_PATH}`;
  }

  protected leaveFor(url: string): void {
    window.location.assign(url);
  }

  private purgeExpired(): void {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX) && !this.peekDraft(key.slice(DRAFT_PREFIX.length))) {
        localStorage.removeItem(key);
      }
    }
  }
}

function randomToken(bytes: number): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

async function sha256Base64Url(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
